/// <reference lib="dom" />

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Check,
  Clock,
  ExternalLink,
  MapPin,
  Send,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { api } from "../api.ts";
import { db } from "../db.ts";
import { Spinner } from "../components/Spinner.tsx";
import { Button, Input } from "../components/ui/index.ts";
import type { PublicEventEntry } from "../../../src/eventPage.ts";

interface FriendInfo {
  id: string;
  phone: string;
  name?: string;
}

interface IncomingRequest {
  id: string;
  fromPhone: string;
  fromName?: string;
  createdAt: number;
}

interface OutgoingRequest {
  id: string;
  toPhone: string;
  createdAt: number;
}

interface FriendsData {
  userPhone?: string;
  userName?: string;
  friends: FriendInfo[];
  incomingRequests: IncomingRequest[];
  outgoingRequests: OutgoingRequest[];
}

interface FriendEventItem {
  event: PublicEventEntry;
  interestedFriends: string[];
}

export const FriendsPageContent = () => {
  const { user } = db.useAuth();
  const [data, setData] = useState<FriendsData | null>(null);
  const [friendsEvents, setFriendsEvents] = useState<FriendEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [phoneInput, setPhoneInput] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);
  const [feedback, setFeedback] = useState<
    { type: "error" | "success"; text: string } | null
  >(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const fetchFriendsData = async () => {
    if (!user) return;
    try {
      const res = await api({
        endpoint: "getFriendsData",
        payload: {},
      });
      setData(res);
    } catch (err: unknown) {
      console.error("Failed to load friends data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFriendsEvents = async () => {
    if (!user) return;
    setEventsLoading(true);
    try {
      const res = await api({
        endpoint: "getFriendsEvents",
        payload: {},
      });
      setFriendsEvents(res);
    } catch (err: unknown) {
      console.error("Failed to load friends events:", err);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchFriendsData();
    fetchFriendsEvents();
  }, [user?.id]);

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneInput.trim()) return;

    setSendingRequest(true);
    setFeedback(null);

    try {
      const res = await api({
        endpoint: "sendFriendRequest",
        payload: { friendPhone: phoneInput.trim() },
      });

      if (res.ok) {
        setFeedback({ type: "success", text: res.message });
        setPhoneInput("");
        await fetchFriendsData();
      } else {
        setFeedback({ type: "error", text: res.message });
      }
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error
          ? err.message
          : "Failed to send friend request",
      });
    } finally {
      setSendingRequest(false);
    }
  };

  const handleRespond = async (
    requestId: string,
    action: "accept" | "decline" | "cancel",
  ) => {
    setActionLoadingId(requestId);
    setFeedback(null);
    try {
      const res = await api({
        endpoint: "respondFriendRequest",
        payload: { requestId, action },
      });
      if (res.ok) {
        setFeedback({ type: "success", text: res.message });
        await fetchFriendsData();
        if (action === "accept") {
          await fetchFriendsEvents();
        }
      } else {
        setFeedback({ type: "error", text: res.message });
      }
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to process request",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRemoveFriend = async (
    friendPhone: string,
    friendName?: string,
  ) => {
    const displayName = friendName ?? friendPhone;
    if (
      !confirm(
        `Are you sure you want to remove ${displayName} from your friends?`,
      )
    ) {
      return;
    }

    setActionLoadingId(friendPhone);
    setFeedback(null);
    try {
      const res = await api({
        endpoint: "removeFriend",
        payload: { friendPhone },
      });
      if (res.ok) {
        setFeedback({ type: "success", text: res.message });
        await fetchFriendsData();
        await fetchFriendsEvents();
      } else {
        setFeedback({ type: "error", text: res.message });
      }
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to remove friend",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner className="h-8 w-8 text-purple-400" />
      </div>
    );
  }

  const friends = data?.friends ?? [];
  const incoming = data?.incomingRequests ?? [];
  const outgoing = data?.outgoingRequests ?? [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-3">
          <Users className="w-8 h-8 text-purple-400" />
          Friends
        </h1>
        <p className="text-gray-400 mt-2 text-sm sm:text-base">
          Connect symmetrically with friends. When you mark an event as
          interested, you can notify them and see what they're attending.
        </p>
      </div>

      {/* Feedback banner */}
      {feedback && (
        <div
          className={`p-4 rounded-xl text-sm font-medium border ${
            feedback.type === "success"
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
              : "bg-rose-500/10 border-rose-500/30 text-rose-300"
          }`}
        >
          {feedback.text}
        </div>
      )}

      {/* Add Friend Form */}
      <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 shadow-xl backdrop-blur-md">
        <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-3">
          <UserPlus className="w-5 h-5 text-purple-400" />
          Add a Friend
        </h2>
        <p className="text-xs text-gray-400 mb-4">
          Enter their phone number with international country prefix (e.g.
          +972501234567 or +12125551234). They will receive a notification to
          confirm.
        </p>
        <form
          onSubmit={handleSendRequest}
          className="flex flex-col sm:flex-row gap-3"
        >
          <div className="flex-1">
            <Input
              type="tel"
              placeholder="+972501234567"
              value={phoneInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPhoneInput(e.target.value)}
              className="w-full bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus:border-purple-400"
            />
          </div>
          <Button
            type="submit"
            disabled={sendingRequest || !phoneInput.trim()}
            className="bg-purple-600 hover:bg-purple-500 text-white font-semibold px-6 shrink-0 flex items-center justify-center gap-2"
          >
            {sendingRequest
              ? <Spinner className="w-4 h-4" />
              : <Send className="w-4 h-4" />}
            Send Request
          </Button>
        </form>
      </div>

      {/* Incoming Requests */}
      {incoming.length > 0 && (
        <div className="bg-purple-950/20 border border-purple-500/30 rounded-2xl p-6 shadow-xl backdrop-blur-md">
          <h2 className="text-lg font-bold text-purple-200 flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-purple-400" />
            Pending Friend Requests ({incoming.length})
          </h2>
          <div className="space-y-3">
            {incoming.map((req) => (
              <div
                key={req.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-white/[0.04] border border-white/10"
              >
                <div>
                  <p className="font-semibold text-white">
                    {req.fromName ? req.fromName : req.fromPhone}
                  </p>
                  {req.fromName && (
                    <p className="text-xs text-gray-400">{req.fromPhone}</p>
                  )}
                  <p className="text-xs text-purple-300/70 mt-0.5">
                    Sent {new Date(req.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleRespond(req.id, "accept")}
                    disabled={actionLoadingId === req.id}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-4 py-2 flex items-center gap-1.5"
                  >
                    {actionLoadingId === req.id
                      ? <Spinner className="w-3.5 h-3.5" />
                      : <Check className="w-3.5 h-3.5" />}
                    Confirm
                  </Button>
                  <Button
                    onClick={() => handleRespond(req.id, "decline")}
                    disabled={actionLoadingId === req.id}
                    variant="outline"
                    className="border-white/10 hover:bg-white/10 text-gray-300 text-xs px-3 py-2 flex items-center gap-1.5"
                  >
                    <X className="w-3.5 h-3.5 text-rose-400" />
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outgoing Requests */}
      {outgoing.length > 0 && (
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">
            Sent Requests (Waiting for confirmation)
          </h2>
          <div className="space-y-2">
            {outgoing.map((req) => (
              <div
                key={req.id}
                className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/5 text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="text-gray-300 font-medium">
                    {req.toPhone}
                  </span>
                  <span className="text-[11px] bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-2 py-0.5 rounded-full">
                    Pending
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRespond(req.id, "cancel")}
                  disabled={actionLoadingId === req.id}
                  className="text-xs text-gray-400 hover:text-rose-300 transition-colors p-1"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmed Mutual Friends */}
      <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 shadow-xl backdrop-blur-md">
        <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
          <UserCheck className="w-5 h-5 text-emerald-400" />
          My Friends ({friends.length})
        </h2>

        {friends.length === 0
          ? (
            <div className="text-center py-10 text-gray-400 space-y-2">
              <Users className="w-10 h-10 mx-auto text-gray-600" />
              <p className="text-sm font-medium">
                You don't have any confirmed friends yet.
              </p>
              <p className="text-xs text-gray-500">
                Add friends above to see what events they're interested in and
                notify them when you find cool events!
              </p>
            </div>
          )
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {friends.map((friend) => (
                <div
                  key={friend.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-white truncate">
                      {friend.name ? friend.name : friend.phone}
                    </p>
                    {friend.name && (
                      <p className="text-xs text-gray-400 truncate">
                        {friend.phone}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveFriend(friend.phone, friend.name)}
                    disabled={actionLoadingId === friend.phone}
                    title="Remove friend"
                    className="p-2 text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors ml-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Friends' Events Section */}
      {friends.length > 0 && (
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 shadow-xl backdrop-blur-md space-y-4">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-400" />
              Events Your Friends Are Interested In
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              Upcoming events your mutual friends marked interest in.
            </p>
          </div>

          {eventsLoading
            ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-6 h-6 text-purple-400" />
              </div>
            )
            : friendsEvents.length === 0
            ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                None of your friends have shared upcoming events yet.
              </div>
            )
            : (
              <div className="space-y-3">
                {friendsEvents.map(({ event, interestedFriends }) => (
                  <div
                    key={event.id}
                    className="p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/40 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="space-y-1">
                      <Link
                        to={`/event/${event.id}`}
                        className="font-bold text-white hover:text-purple-300 transition-colors flex items-center gap-1.5"
                      >
                        {event.structured.name}
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      </Link>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-purple-400" />
                          {new Date(
                            event.structured.timestamp.start,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                        {event.structured.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-purple-400" />
                            {event.structured.city}
                          </span>
                        )}
                      </div>
                      <div className="pt-1">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-200 border border-purple-500/30">
                          <Users className="w-3 h-3 text-purple-300" />
                          {interestedFriends.join(", ")}{" "}
                          {interestedFriends.length === 1 ? "is" : "are"}{" "}
                          interested
                        </span>
                      </div>
                    </div>

                    <Link
                      to={`/event/${event.id}`}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-purple-600/80 hover:bg-purple-600 text-white transition-colors shrink-0"
                    >
                      View Event
                    </Link>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
};
